import { useState, useEffect, useCallback } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Radio, Copy, Check, Loader2, ExternalLink, AlertTriangle, ChevronDown, ChevronUp, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";

const LC_LABELS = {
  ready: { label: "Ready — waiting for stream", cls: "bg-[#F1EEE8] text-[#8FA89B]" },
  testStarting: { label: "Test starting…", cls: "bg-[#D98E73]/15 text-[#B5794E]" },
  testing: { label: "Testing", cls: "bg-[#D98E73]/20 text-[#B5794E]" },
  liveStarting: { label: "Going live…", cls: "bg-[#5C8A72]/15 text-[#5C8A72]" },
  live: { label: "LIVE", cls: "bg-[#5C8A72] text-white font-bold animate-pulse" },
  complete: { label: "Ended", cls: "bg-[#F1EEE8] text-[#8FA89B]" },
  revoked: { label: "Revoked", cls: "bg-[#B45151]/10 text-[#B45151]" },
};

function useCopyToClipboard() {
  const [copied, setCopied] = useState("");
  const copy = useCallback((text, key) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(key);
      setTimeout(() => setCopied(""), 2000);
    });
  }, []);
  return { copy, copied };
}

function SecretField({ label, value, copyKey, copy, copied, testId }) {
  const [shown, setShown] = useState(false);
  return (
    <div className="rounded-lg bg-[#0D1A22] p-3 font-mono text-sm">
      <div className="flex items-center justify-between mb-1">
        <span className="text-[#8FA89B] text-xs uppercase tracking-wide">{label}</span>
        <div className="flex items-center gap-2">
          <button onClick={() => setShown(!shown)} className="text-[#8FA89B] hover:text-white transition-colors text-xs" data-testid={`${testId}-toggle`}>
            {shown ? "Hide" : "Reveal"}
          </button>
          <button onClick={() => copy(value, copyKey)} className="text-[#8FA89B] hover:text-[#5C8A72] transition-colors" data-testid={`${testId}-copy`} title="Copy">
            {copied === copyKey ? <Check size={13} className="text-[#5C8A72]" /> : <Copy size={13} />}
          </button>
        </div>
      </div>
      <p className="text-[#E8E3DC] break-all select-all" data-testid={testId}>
        {shown ? value : "•".repeat(Math.min(value.length, 40))}
      </p>
    </div>
  );
}

function BroadcastRow({ b, onRefresh }) {
  const [open, setOpen] = useState(false);
  const [streamOk, setStreamOk] = useState(null);
  const [polling, setPolling] = useState(false);
  const { copy, copied } = useCopyToClipboard();
  const qc = useQueryClient();

  const transitionMut = useMutation({
    mutationFn: ({ id, status }) => api.post(`/youtube/live/broadcasts/${id}/transition/${status}`).then((r) => r.data),
    onSuccess: (data) => {
      toast.success(`Broadcast is now: ${data.lifecycle_status}`);
      qc.invalidateQueries({ queryKey: ["yt-broadcasts"] });
    },
    onError: (e) => {
      const msg = e.response?.data?.detail || "Failed to transition broadcast";
      if (typeof msg === "string" && msg.includes("reconnect")) {
        toast.error("YouTube token expired — please reconnect in Integrations.");
      } else {
        toast.error(msg);
      }
    },
  });

  const checkStream = async () => {
    setPolling(true);
    try {
      const { data } = await api.get(`/youtube/live/broadcasts/${b.broadcast_id}/stream-status`);
      setStreamOk(data.stream_active);
      if (data.lifecycle_status !== b.lifecycle_status) onRefresh();
    } catch {
      setStreamOk(false);
    } finally {
      setPolling(false);
    }
  };

  const lc = b.lifecycle_status || "ready";
  const lcUi = LC_LABELS[lc] || LC_LABELS.ready;
  const isEnded = lc === "complete" || lc === "revoked";
  const isLive = lc === "live";
  const canGoLive = lc === "ready" || lc === "testing";

  return (
    <div className="border border-[#E8E3DC] rounded-xl overflow-hidden" data-testid={`yt-broadcast-${b.broadcast_id}`}>
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between p-4 text-left hover:bg-[#F9F7F4] transition-colors">
        <div className="flex items-center gap-3 min-w-0">
          <Radio size={16} className={isLive ? "text-[#5C8A72] animate-pulse" : "text-[#8FA89B]"} />
          <div className="min-w-0">
            <p className="font-medium text-[#1A242B] truncate text-sm">{b.title}</p>
            <p className="text-xs text-[#8FA89B]">{b.broadcast_id}</p>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0 ml-2">
          <span className={`text-xs px-2 py-0.5 rounded-full ${lcUi.cls}`}>{lcUi.label}</span>
          {open ? <ChevronUp size={14} className="text-[#8FA89B]" /> : <ChevronDown size={14} className="text-[#8FA89B]" />}
        </div>
      </button>

      {open && (
        <div className="border-t border-[#E8E3DC] p-4 space-y-3 bg-[#FAFAF8]">
          {/* Stream credentials */}
          {!isEnded && (
            <>
              <p className="text-xs text-[#4A5D6B] font-medium">Configure your encoder (OBS) with these settings:</p>
              <SecretField label="RTMPS Server URL" value={b.ingestion_url || ""} copyKey="server" copy={copy} copied={copied} testId={`yt-server-${b.broadcast_id}`} />
              <SecretField label="Stream Key (keep private)" value={b.stream_name || ""} copyKey="key" copy={copy} copied={copied} testId={`yt-key-${b.broadcast_id}`} />
            </>
          )}

          {/* Stream health check */}
          {!isEnded && (
            <div className="flex items-center gap-3">
              <button onClick={checkStream} disabled={polling} className="eyw-btn-ghost !py-1.5 text-xs flex items-center gap-1.5" data-testid={`yt-check-stream-${b.broadcast_id}`}>
                {polling ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />} Check stream
              </button>
              {streamOk !== null && (
                <span className={`text-xs flex items-center gap-1 ${streamOk ? "text-[#5C8A72]" : "text-[#B5794E]"}`}>
                  {streamOk ? <Check size={12} /> : <AlertTriangle size={12} />}
                  {streamOk ? "Encoder connected" : "No encoder signal yet"}
                </span>
              )}
            </div>
          )}

          {/* Action buttons */}
          <div className="flex items-center gap-2 flex-wrap pt-1">
            {isLive && (
              <a href={b.watch_url} target="_blank" rel="noreferrer" className="eyw-btn !py-2 text-sm" data-testid={`yt-watch-${b.broadcast_id}`}>
                <ExternalLink size={14} /> Watch on YouTube
              </a>
            )}
            {canGoLive && streamOk !== false && (
              <button
                onClick={() => transitionMut.mutate({ id: b.broadcast_id, status: "live" })}
                disabled={transitionMut.isPending}
                data-testid={`yt-go-live-${b.broadcast_id}`}
                className="eyw-btn !py-2 text-sm disabled:opacity-60 bg-[#5C8A72] text-white hover:bg-[#4A7660]"
              >
                {transitionMut.isPending ? <Loader2 size={14} className="animate-spin" /> : <Radio size={14} />}
                Go Live
              </button>
            )}
            {(isLive || lc === "testing") && (
              <button
                onClick={() => transitionMut.mutate({ id: b.broadcast_id, status: "complete" })}
                disabled={transitionMut.isPending}
                data-testid={`yt-end-${b.broadcast_id}`}
                className="eyw-btn-ghost !py-2 text-sm text-[#B45151]"
              >
                End broadcast
              </button>
            )}
            {isLive && (
              <button onClick={() => { copy(b.watch_url, "watch"); toast.success("Watch URL copied!"); }} className="eyw-btn-ghost !py-2 text-sm" data-testid={`yt-copy-watch-${b.broadcast_id}`}>
                {copied === "watch" ? <Check size={14} /> : <Copy size={14} />} Copy watch URL
              </button>
            )}
          </div>

          {isEnded && (
            <p className="text-xs text-[#8FA89B]">This broadcast has ended.
              {b.watch_url && <> <a href={b.watch_url} target="_blank" rel="noreferrer" className="underline ml-1">View recording on YouTube</a></>}
            </p>
          )}
        </div>
      )}
    </div>
  );
}

export default function YouTubeLiveCard() {
  const qc = useQueryClient();
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ title: "", description: "", privacy_status: "private" });

  const { data, isLoading, refetch } = useQuery({
    queryKey: ["yt-broadcasts"],
    queryFn: () => api.get("/youtube/live/broadcasts").then((r) => r.data),
    staleTime: 30000,
  });

  const createMut = useMutation({
    mutationFn: (body) => api.post("/youtube/live/broadcasts", body).then((r) => r.data),
    onSuccess: () => {
      toast.success("YouTube Live broadcast created! Configure your encoder below.");
      qc.invalidateQueries({ queryKey: ["yt-broadcasts"] });
      setShowCreate(false);
      setForm({ title: "", description: "", privacy_status: "private" });
    },
    onError: (e) => {
      const msg = e.response?.data?.detail;
      if (typeof msg === "string") {
        if (msg.includes("reconnect")) {
          toast.error("YouTube session expired. Please reconnect YouTube Live in Integrations.");
        } else if (msg.includes("not enabled")) {
          toast.error(msg);
        } else {
          toast.error(formatApiError(msg) || "Could not create broadcast");
        }
      } else {
        toast.error("Could not create broadcast");
      }
    },
  });

  const broadcasts = data?.broadcasts || [];
  const active = broadcasts.filter((b) => !["complete", "revoked"].includes(b.lifecycle_status));
  const past = broadcasts.filter((b) => ["complete", "revoked"].includes(b.lifecycle_status));

  return (
    <div data-testid="yt-live-card" className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm font-medium text-[#1A242B]">Live Broadcasts</p>
        <button onClick={() => setShowCreate(!showCreate)} data-testid="yt-create-btn" className="eyw-btn !py-2 text-xs">
          {showCreate ? "Cancel" : "+ New Broadcast"}
        </button>
      </div>

      {/* Create broadcast form */}
      {showCreate && (
        <div className="rounded-xl border border-[#E8E3DC] p-4 space-y-3 bg-white" data-testid="yt-create-form">
          <input
            className="eyw-input w-full text-sm"
            placeholder="Broadcast title"
            value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
            maxLength={100}
            data-testid="yt-title-input"
          />
          <textarea
            className="eyw-input w-full text-sm resize-none"
            placeholder="Description (optional)"
            rows={2}
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            data-testid="yt-desc-input"
          />
          <select
            className="eyw-input w-full text-sm"
            value={form.privacy_status}
            onChange={(e) => setForm({ ...form, privacy_status: e.target.value })}
            data-testid="yt-privacy-select"
          >
            <option value="private">Private</option>
            <option value="unlisted">Unlisted</option>
            <option value="public">Public</option>
          </select>
          <p className="text-xs text-[#8FA89B]">After creating, you'll get RTMPS server URL and stream key. Use them in OBS or any streaming encoder to connect and go live.</p>
          <button
            onClick={() => createMut.mutate(form)}
            disabled={createMut.isPending || !form.title.trim()}
            data-testid="yt-create-submit"
            className="eyw-btn !py-2 text-sm w-full disabled:opacity-60"
          >
            {createMut.isPending ? <><Loader2 size={14} className="animate-spin" /> Creating…</> : <><Radio size={14} /> Create broadcast</>}
          </button>
        </div>
      )}

      {/* Active broadcasts */}
      {isLoading ? (
        <div className="text-center py-6 text-[#8FA89B]"><Loader2 className="animate-spin inline" size={18} /></div>
      ) : active.length > 0 ? (
        <div className="space-y-2">
          {active.map((b) => <BroadcastRow key={b.broadcast_id} b={b} onRefresh={refetch} />)}
        </div>
      ) : (
        <p className="text-sm text-[#8FA89B] text-center py-4">No active broadcasts. Create one above to get started.</p>
      )}

      {/* Past broadcasts */}
      {past.length > 0 && (
        <details className="text-xs text-[#8FA89B]">
          <summary className="cursor-pointer hover:text-[#4A5D6B] py-1">Past broadcasts ({past.length})</summary>
          <div className="mt-2 space-y-2">
            {past.slice(0, 5).map((b) => <BroadcastRow key={b.broadcast_id} b={b} onRefresh={refetch} />)}
          </div>
        </details>
      )}
    </div>
  );
}
